import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Users,
  FolderGit2,
  X,
  AlertTriangle,
  Power,
  PowerOff,
  Shield,
  CheckCircle2,
} from "lucide-react";
import type { Team } from "@/types/admin";
import adminService from "@/services/admin-service";
import { Card, CardBody } from "@/components/ui/Card";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import { Permission } from "@/types";
import { usePagePermission } from "@/hooks/usePagePermission.tsx";

interface CreateTeamForm {
  team_name: string;
  description: string;
}

interface EditTeamForm {
  team_name: string;
  description: string;
}

interface DeleteConfirmation {
  team: Team;
  isOpen: boolean;
}

export default function TeamsPage() {
  const navigate = useNavigate();
  const { hasAccess, AccessDenied403 } = usePagePermission(
    Permission.READ_TEAM,
  );
  const { hasPermission } = useAuthStore();

  // Check write/delete permissions
  const canWriteTeams = hasPermission(Permission.WRITE_TEAM);
  const canDeleteTeams = hasPermission(Permission.DELETE_TEAM);

  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] =
    useState<DeleteConfirmation>({ team: {} as Team, isOpen: false });
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyTeam, setPolicyTeam] = useState<Team | null>(null);

  // Form states
  const [createForm, setCreateForm] = useState<CreateTeamForm>({
    team_name: "",
    description: "",
  });
  const [editForm, setEditForm] = useState<EditTeamForm>({
    team_name: "",
    description: "",
  });
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);

  // Loading states
  const [submitting, setSubmitting] = useState(false);

  // Policy states
  const [teamPolicy, setTeamPolicy] = useState<any>(null);
  const [loadingPolicy, setLoadingPolicy] = useState(false);
  const [policyServices, setPolicyServices] = useState<string[]>([]);
  const [availableServices, setAvailableServices] = useState<string[]>([]);
  const [newService, setNewService] = useState("");

  useEffect(() => {
    // Only load if user has permission
    if (hasAccess) {
      loadTeams();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const loadTeams = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.listTeams();
      setTeams(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load teams");
      console.error("Error loading teams:", err);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.team_name) {
      setError("Team name is required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await adminService.createTeam({
        team_name: createForm.team_name,
        description: createForm.description || undefined,
      });

      setShowCreateModal(false);
      setCreateForm({ team_name: "", description: "" });
      await loadTeams(); // Reload teams list
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to create team");
      console.error("Error creating team:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTeam || !editForm.team_name) {
      setError("Team name is required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await adminService.updateTeam(editingTeam.team_id, {
        team_name: editForm.team_name,
        description: editForm.description || undefined,
      });

      setShowEditModal(false);
      setEditingTeam(null);
      await loadTeams(); // Reload teams list
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to update team");
      console.error("Error updating team:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!deleteConfirmation.team?.team_id) return;

    try {
      setSubmitting(true);
      setError(null);

      await adminService.deleteTeam(deleteConfirmation.team.team_id);

      setDeleteConfirmation({ team: {} as Team, isOpen: false });
      await loadTeams(); // Reload teams list
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to delete team");
      console.error("Error deleting team:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateModal = () => {
    setCreateForm({ team_name: "", description: "" });
    setShowCreateModal(true);
  };

  const openEditModal = (team: Team) => {
    setEditingTeam(team);
    setEditForm({
      team_name: team.team_name,
      description: team.description || "",
    });
    setShowEditModal(true);
  };

  const openDeleteConfirmation = (team: Team) => {
    setDeleteConfirmation({ team, isOpen: true });
  };

  const openPolicyModal = async (team: Team) => {
    setPolicyTeam(team);
    setLoadingPolicy(true);
    setShowPolicyModal(true);

    try {
      // Load current policy
      const policy = await adminService.getTeamPolicy(team.team_id);
      setTeamPolicy(policy);
      setPolicyServices(policy.allowed_services || []);

      // Load available services from routes
      const routes = await adminService.listRoutes();
      const services = routes.map((r: any) => r.service_name);
      setAvailableServices(services);
    } catch (err: any) {
      console.error("Error loading policy:", err);
      setTeamPolicy({ has_policy: false });
      setPolicyServices([]);

      // Still load available services
      try {
        const routes = await adminService.listRoutes();
        const services = routes.map((r: any) => r.service_name);
        setAvailableServices(services);
      } catch (e) {
        console.error("Error loading services:", e);
      }
    } finally {
      setLoadingPolicy(false);
    }
  };

  const handleSavePolicy = async () => {
    if (!policyTeam) return;

    try {
      setSubmitting(true);
      setError(null);

      const policyData = {
        allowed_services: policyServices,
        enabled: true,
        description: `Service policy for ${policyTeam.team_name}`,
      };

      if (teamPolicy?.has_policy) {
        // Update existing policy
        await adminService.updateTeamPolicy(policyTeam.team_id, policyData);
      } else {
        // Create new policy
        await adminService.createTeamPolicy(policyTeam.team_id, policyData);
      }

      setShowPolicyModal(false);
      setPolicyTeam(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save policy");
      console.error("Error saving policy:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const addService = () => {
    if (newService && !policyServices.includes(newService)) {
      setPolicyServices([...policyServices, newService]);
      setNewService("");
    }
  };

  const removeService = (service: string) => {
    setPolicyServices(policyServices.filter((s) => s !== service));
  };

  const handleToggleStatus = async (team: Team) => {
    const newStatus = team.status === "active" ? "inactive" : "active";
    const action = newStatus === "active" ? "activate" : "deactivate";

    if (
      !confirm(`Are you sure you want to ${action} team "${team.team_name}"?`)
    ) {
      return;
    }

    try {
      setError(null);
      const updatedTeam = await adminService.updateTeamStatus(
        team.team_id,
        newStatus,
      );

      console.log(`Team ${team.team_name} ${action}d successfully.`, {
        oldStatus: team.status,
        newStatus: updatedTeam.status,
        updatedTeam,
      });

      // Update the team in the local state immediately for better UX
      setTeams((prevTeams) => {
        const newTeams = prevTeams.map((t) =>
          t.team_id === updatedTeam.team_id ? updatedTeam : t,
        );
        console.log(
          "Updated teams state:",
          newTeams.map((t) => ({ name: t.team_name, status: t.status })),
        );
        return newTeams;
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to ${action} team`);
      console.error(`Error ${action}ing team:`, err);
    }
  };

  const filteredTeams = (teams || []).filter((team) => {
    const matchesSearch =
      team.team_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const teamStatus = team.status || "active"; // Default to active if status is missing
    const matchesStatus = statusFilter === "all" || teamStatus === statusFilter;

    // Debug logging
    if (team.team_name === "Atlas CLI Evals") {
      console.log("Filtering Atlas CLI Evals:", {
        teamStatus,
        statusFilter,
        matchesStatus,
        matchesSearch,
        willShow: matchesSearch && matchesStatus,
      });
    }

    return matchesSearch && matchesStatus;
  });

  // Show 403 if no permission
  if (!hasAccess) {
    return <AccessDenied403 />;
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-600 via-teal-600 to-blue-600 p-8 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative flex items-center justify-between">
          <div>
            <h1 className="mb-2 text-4xl font-bold">Team Management</h1>
            <p className="text-lg text-green-100">
              Organize and manage your teams across the organization
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden rounded-xl bg-white/10 px-4 py-2 backdrop-blur-sm sm:block">
              <p className="text-sm text-green-100">Total Teams</p>
              <p className="text-2xl font-bold">{filteredTeams.length}</p>
            </div>
            {canWriteTeams && (
              <button
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 rounded-xl bg-white/20 px-4 py-3 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/30"
              >
                <Plus className="h-4 w-4" />
                Create Team
              </button>
            )}
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="absolute -bottom-4 -right-12 h-32 w-32 rounded-full bg-white/5"></div>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardBody>
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Search and Filter */}
      <Card>
        <CardBody>
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search teams by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-gray-50/50 py-3 pl-10 pr-4 text-sm placeholder-gray-400 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white dark:placeholder-gray-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(
                    e.target.value as "all" | "active" | "inactive",
                  )
                }
                className="rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
              <div className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {filteredTeams.length} teams
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Teams Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredTeams.map((team, index) => (
          <Card key={team.team_id} hover shadow="lg" className="group">
            <div className="relative">
              {/* Gradient header */}
              <div
                className={cn(
                  "h-20 rounded-t-xl bg-gradient-to-r",
                  index % 4 === 0 && "from-blue-400 to-purple-600",
                  index % 4 === 1 && "from-green-400 to-teal-600",
                  index % 4 === 2 && "from-orange-400 to-red-500",
                  index % 4 === 3 && "from-purple-400 to-pink-600",
                )}
              >
                <div className="absolute -bottom-6 left-6">
                  <div
                    className={cn(
                      "flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r text-white shadow-lg ring-4 ring-white dark:ring-gray-800",
                      index % 4 === 0 && "from-blue-500 to-purple-600",
                      index % 4 === 1 && "from-green-500 to-teal-600",
                      index % 4 === 2 && "from-orange-500 to-red-600",
                      index % 4 === 3 && "from-purple-500 to-pink-600",
                    )}
                  >
                    <Users className="h-6 w-6" />
                  </div>
                </div>
              </div>

              {/* Status badge */}
              <div className="absolute right-4 top-4">
                <span
                  className={cn(
                    "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold shadow-sm",
                    team.status === "active"
                      ? "bg-white/90 text-green-800"
                      : "bg-white/90 text-gray-600",
                  )}
                >
                  <div
                    className={cn(
                      "mr-2 h-2 w-2 rounded-full",
                      team.status === "active" ? "bg-green-500" : "bg-gray-400",
                    )}
                  />
                  {team.status === "active" ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            <CardBody className="pt-8">
              <div className="space-y-4">
                <div className={team.description ? "" : "mb-2"}>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {team.team_name}
                  </h3>
                  {team.description ? (
                    <p className="mt-2 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                      {team.description}
                    </p>
                  ) : (
                    <p className="mt-2 text-sm italic text-gray-400 dark:text-gray-500">
                      No description provided
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                      <Users className="h-4 w-4" />
                      <span>{team.stats?.member_count || 0} members</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                      <FolderGit2 className="h-4 w-4" />
                      <span>{team.stats?.project_count || 0} projects</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 pt-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        navigate({
                          to: "/admin/users",
                          search: { team_id: team.team_id },
                        })
                      }
                      className="flex-1 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-all hover:from-gray-100 hover:to-gray-200 dark:from-gray-700 dark:to-gray-600 dark:text-gray-300 dark:hover:from-gray-600 dark:hover:to-gray-500"
                    >
                      View Members
                    </button>
                    {canWriteTeams && (
                      <button
                        onClick={() => handleToggleStatus(team)}
                        className={cn(
                          "rounded-xl p-2 transition-colors",
                          team.status === "active"
                            ? "text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                            : "text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20",
                        )}
                        title={
                          team.status === "active"
                            ? "Deactivate team"
                            : "Activate team"
                        }
                      >
                        {team.status === "active" ? (
                          <PowerOff className="h-4 w-4" />
                        ) : (
                          <Power className="h-4 w-4" />
                        )}
                      </button>
                    )}
                    {canWriteTeams && (
                      <button
                        onClick={() => openEditModal(team)}
                        className="rounded-xl p-2 text-blue-600 transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20"
                        title="Edit team"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    )}
                    {canDeleteTeams && (
                      <button
                        onClick={() => openDeleteConfirmation(team)}
                        className="rounded-xl p-2 text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Delete team"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {canWriteTeams && (
                    <button
                      onClick={() => openPolicyModal(team)}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 px-4 py-2 text-sm font-medium text-purple-700 transition-all hover:from-purple-100 hover:to-indigo-100 dark:from-purple-900/20 dark:to-indigo-900/20 dark:text-purple-300 dark:hover:from-purple-900/30 dark:hover:to-indigo-900/30"
                    >
                      <Shield className="h-4 w-4" />
                      Service Policy
                    </button>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Enhanced Empty State */}
      {filteredTeams.length === 0 && (
        <Card>
          <CardBody>
            <div className="py-12 text-center">
              <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-green-100 to-teal-200 dark:from-green-700 dark:to-teal-600">
                <Users className="h-12 w-12 text-green-600 dark:text-green-300" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                No teams found
              </h3>
              <p className="mx-auto max-w-sm text-gray-500 dark:text-gray-400">
                {searchTerm
                  ? "Try adjusting your search term to find what you're looking for."
                  : "Teams help organize your users and projects. Create your first team to get started."}
              </p>
              {!searchTerm && canWriteTeams && (
                <button
                  onClick={openCreateModal}
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-green-500 to-teal-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-colors hover:from-green-600 hover:to-teal-700"
                >
                  <Plus className="h-4 w-4" />
                  Create Your First Team
                </button>
              )}
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  Clear search
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto max-w-md p-6">
            <Card shadow="xl" className="border-0 shadow-2xl">
              <div className="relative">
                {/* Gradient header */}
                <div className="h-20 rounded-t-xl bg-gradient-to-r from-green-500 to-teal-600">
                  <div className="absolute -bottom-6 left-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-green-600 to-teal-700 text-white shadow-lg ring-4 ring-white dark:ring-gray-800">
                      <Plus className="h-6 w-6" />
                    </div>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <CardBody className="pt-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Create New Team
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Add a new team to organize your users and projects
                  </p>
                </div>

                <form onSubmit={handleCreateTeam} className="space-y-6">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Team Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={createForm.team_name}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          team_name: e.target.value,
                        })
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                      placeholder="Enter team name"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Description
                    </label>
                    <textarea
                      rows={3}
                      value={createForm.description}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          description: e.target.value,
                        })
                      }
                      className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                      placeholder="Describe the team's purpose"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 rounded-xl bg-gradient-to-r from-green-500 to-teal-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-green-600 hover:to-teal-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitting ? "Creating..." : "Create Team"}
                    </button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditModal && editingTeam && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto max-w-md p-6">
            <Card shadow="xl" className="border-0 shadow-2xl">
              <div className="relative">
                {/* Gradient header */}
                <div className="h-20 rounded-t-xl bg-gradient-to-r from-blue-500 to-indigo-600">
                  <div className="absolute -bottom-6 left-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg ring-4 ring-white dark:ring-gray-800">
                      <Edit2 className="h-6 w-6" />
                    </div>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => setShowEditModal(false)}
                  className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <CardBody className="pt-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Edit Team
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Update team information and settings
                  </p>
                </div>

                <form onSubmit={handleEditTeam} className="space-y-6">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Team Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={editForm.team_name}
                      onChange={(e) =>
                        setEditForm({ ...editForm, team_name: e.target.value })
                      }
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                      Description
                    </label>
                    <textarea
                      rows={3}
                      value={editForm.description}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          description: e.target.value,
                        })
                      }
                      className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-blue-600 hover:to-indigo-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitting ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto max-w-md p-6">
            <Card shadow="xl" className="border-0 shadow-2xl">
              <div className="relative">
                {/* Gradient header */}
                <div className="h-20 rounded-t-xl bg-gradient-to-r from-red-500 to-pink-600">
                  <div className="absolute -bottom-6 left-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-red-600 to-pink-700 text-white shadow-lg ring-4 ring-white dark:ring-gray-800">
                      <AlertTriangle className="h-6 w-6" />
                    </div>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() =>
                    setDeleteConfirmation({ team: {} as Team, isOpen: false })
                  }
                  className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <CardBody className="pt-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Delete Team
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    This action cannot be undone
                  </p>
                </div>

                <div className="mb-6">
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                    <p className="text-sm text-red-800 dark:text-red-200">
                      Are you sure you want to delete{" "}
                      <strong className="font-semibold">
                        {deleteConfirmation.team.team_name}
                      </strong>
                      ? This will permanently remove all team members and
                      associated projects.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setDeleteConfirmation({ team: {} as Team, isOpen: false })
                    }
                    className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteTeam}
                    disabled={submitting}
                    className="flex-1 rounded-xl bg-gradient-to-r from-red-500 to-pink-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-red-600 hover:to-pink-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {submitting ? "Deleting..." : "Delete Team"}
                  </button>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* Service Policy Modal */}
      {showPolicyModal && policyTeam && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50 backdrop-blur-sm">
          <div className="relative top-10 mx-auto max-w-2xl p-6">
            <Card shadow="xl" className="border-0 shadow-2xl">
              <div className="relative">
                {/* Gradient header */}
                <div className="h-24 rounded-t-xl bg-gradient-to-r from-purple-500 via-indigo-600 to-blue-600">
                  <div className="absolute -bottom-8 left-6">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-700 text-white shadow-xl ring-4 ring-white dark:ring-gray-800">
                      <Shield className="h-8 w-8" />
                    </div>
                  </div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => {
                    setShowPolicyModal(false);
                    setPolicyTeam(null);
                  }}
                  className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <CardBody className="pt-12">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Service Policy
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Configure allowed services for {policyTeam.team_name}
                  </p>
                </div>

                {loadingPolicy ? (
                  <div className="py-8 text-center">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-purple-600 border-r-transparent"></div>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      Loading policy...
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Policy Status */}
                    {teamPolicy?.has_policy ? (
                      <div className="rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                        <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="font-semibold">
                            Policy Configured
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                          API keys for this team can access the configured
                          services.
                        </p>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
                        <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                          <AlertTriangle className="h-5 w-5" />
                          <span className="font-semibold">
                            No Policy Configured
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                          API keys won't work until you configure allowed
                          services.
                        </p>
                      </div>
                    )}

                    {/* Add Service */}
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-gray-900 dark:text-white">
                        Add Service
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={newService}
                          onChange={(e) => setNewService(e.target.value)}
                          className="flex-1 rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                        >
                          <option value="">Select a service...</option>
                          {availableServices
                            .filter((s) => !policyServices.includes(s))
                            .map((service) => (
                              <option key={service} value={service}>
                                {service}
                              </option>
                            ))}
                        </select>
                        <button
                          type="button"
                          onClick={addService}
                          disabled={!newService}
                          className="rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-purple-600 hover:to-indigo-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Add
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Or type "*" to allow all services
                      </p>
                      <input
                        type="text"
                        value={newService}
                        onChange={(e) => setNewService(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && addService()}
                        placeholder="Type service name or *"
                        className="mt-2 w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2 text-sm transition-all focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                      />
                    </div>

                    {/* Allowed Services List */}
                    <div>
                      <label className="mb-3 block text-sm font-semibold text-gray-900 dark:text-white">
                        Allowed Services ({policyServices.length})
                      </label>
                      {policyServices.length === 0 ? (
                        <div className="rounded-xl border-2 border-dashed border-gray-300 p-6 text-center dark:border-gray-700">
                          <Shield className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            No services configured. Add services above.
                          </p>
                        </div>
                      ) : (
                        <div className="max-h-60 space-y-2 overflow-y-auto">
                          {policyServices.map((service) => (
                            <div
                              key={service}
                              className="flex items-center justify-between rounded-xl border border-purple-200 bg-purple-50 p-3 dark:border-purple-800 dark:bg-purple-900/20"
                            >
                              <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                                {service}
                              </span>
                              <button
                                onClick={() => removeService(service)}
                                className="rounded-lg p-1 text-purple-600 transition-colors hover:bg-purple-100 dark:hover:bg-purple-900/40"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowPolicyModal(false);
                          setPolicyTeam(null);
                        }}
                        className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSavePolicy}
                        disabled={submitting || policyServices.length === 0}
                        className="flex-1 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-purple-600 hover:to-indigo-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {submitting
                          ? "Saving..."
                          : teamPolicy?.has_policy
                            ? "Update Policy"
                            : "Create Policy"}
                      </button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
